/**
 * Normalise a video URL into something embeddable.
 *
 * A creator pastes whatever the share button gave them, so every common shape of
 * the big hosts is matched — a bare `watch?v=`-only test missed `youtu.be`,
 * Shorts, live links and any URL carrying extra query params, and those all fell
 * through to a raw iframe of the *page*, which the host then refused to frame
 * (blank box). Anything unrecognised is still framed optimistically; players
 * pair it with an "Open in new tab" escape hatch for exactly that case.
 *
 * ── Why this is its own module ──────────────────────────────────────────────
 * There were two copies: this one, and an older, narrower one in the community
 * feed that knew only watch?v=, embed/ and youtu.be. So a YouTube Short pasted
 * into a lesson played, and the same link pasted into the feed rendered a blank
 * box — the divergence was invisible until someone tried it. One copy cannot
 * drift from itself.
 */
export function toEmbed(url) {
  const u = String(url || "").trim();

  const yt = u.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?rel=0` };

  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return { kind: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}` };

  // Google Drive / Loom share links have dedicated player URLs.
  const drive = u.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  if (drive) return { kind: "iframe", src: `https://drive.google.com/file/d/${drive[1]}/preview` };

  const loom = u.match(/loom\.com\/share\/([\w-]+)/);
  if (loom) return { kind: "iframe", src: `https://www.loom.com/embed/${loom[1]}` };

  if (/\.(mp4|webm|ogg|ogv|mov|m4v)(\?|$)/i.test(u)) return { kind: "video", src: u };

  return { kind: "iframe", src: u };
}

export default toEmbed;
